// src/auth/webauthnHelpers.js
//
// Browser-side WebAuthn/Passkey plumbing — base64url <-> ArrayBuffer
// conversion (the browser's navigator.credentials API needs raw bytes,
// but a JSON API like Authentik's flow executor can only carry base64url
// text; this conversion is inherent to the Web Platform's WebAuthn API,
// not anything specific to Authentik), plus thin wrappers around the two
// actual browser calls.
//
// CONFIRMED live against this project's own Authentik instance
// (2026-09-17), via a raw flow-executor call to the new sso-add-webauthn
// flow: the enrollment challenge's shape is
//   { component: 'ak-stage-authenticator-webauthn', pending_user, pending_user_avatar,
//     registration: { rp, user: { id, name, displayName }, challenge,
//                      pubKeyCredParams, timeout, excludeCredentials,
//                      authenticatorSelection, attestation } }
// — `registration` is otherwise already shaped exactly like the browser's
// own PublicKeyCredentialCreationOptions; only `registration.challenge`
// and `registration.user.id` (and, per spec, any `excludeCredentials[].id`
// entries, confirmed empty in this test but handled generically here
// anyway) are base64url text needing decoding before use.
//
// NOT YET CONFIRMED: the exact shape Authentik's flow executor expects
// when SUBMITTING the completed credential back (that can only be tested
// with a real browser actually completing the WebAuthn ceremony, not a
// server-side script — see this project's own plan file). The encoding
// functions below produce the standard, spec-aligned shape most WebAuthn
// relying parties (Authentik included, going by its own open-source
// client code) expect — verify this the first time SetupPasskey.jsx is
// actually tested, and adjust here if Authentik's response indicates a
// different shape.

function base64UrlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// True only if the browser actually supports WebAuthn at all — gates
// whether any passkey-related UI should render in the first place.
function isWebauthnSupported() {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

function decodeCreationOptions(registration) {
  return {
    ...registration,
    challenge: base64UrlToBuffer(registration.challenge),
    user: { ...registration.user, id: base64UrlToBuffer(registration.user.id) },
    excludeCredentials: (registration.excludeCredentials || []).map((c) => ({
      ...c,
      id: base64UrlToBuffer(c.id),
    })),
  };
}

// Login-time shape is not yet confirmed live (see file header) — built to
// the standard WebAuthn PublicKeyCredentialRequestOptions shape
// (challenge + allowCredentials[].id both base64url-encoded, same as the
// creation side), which is what Authentik's device_challenges entry is
// expected to carry once a passkey exists to validate against.
function decodeRequestOptions(assertion) {
  return {
    ...assertion,
    challenge: base64UrlToBuffer(assertion.challenge),
    allowCredentials: (assertion.allowCredentials || []).map((c) => ({
      ...c,
      id: base64UrlToBuffer(c.id),
    })),
  };
}

// CONFIRMED live (2026-09-17): the generic Object.entries(credential.response)
// walk this used to do came up empty for the fields that actually matter
// (Authentik's own log: "Credential response missing required
// clientDataJSON") — a well-documented WebAuthn gotcha, not a bug in
// Authentik's validation. credential.response is a native
// AuthenticatorAttestationResponse/AuthenticatorAssertionResponse; its
// clientDataJSON/attestationObject/etc. are defined as getters on the
// browser's own object and don't show up as "own enumerable properties"
// the way Object.entries expects — every real-world WebAuthn client reads
// these by explicit name instead, which is what this does now.
function credentialToJSON(credential) {
  const rawResponse = credential.response;
  const response = { clientDataJSON: bufferToBase64Url(rawResponse.clientDataJSON) };
  if (rawResponse.attestationObject) {
    // AuthenticatorAttestationResponse — the create()/enrollment shape.
    response.attestationObject = bufferToBase64Url(rawResponse.attestationObject);
    if (typeof rawResponse.getTransports === 'function') {
      response.transports = rawResponse.getTransports();
    }
  } else {
    // AuthenticatorAssertionResponse — the get()/login shape.
    response.authenticatorData = bufferToBase64Url(rawResponse.authenticatorData);
    response.signature = bufferToBase64Url(rawResponse.signature);
    if (rawResponse.userHandle) {
      response.userHandle = bufferToBase64Url(rawResponse.userHandle);
    }
  }
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response,
    clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {},
  };
}

// CONFIRMED live (2026-09-17), from Authentik's own server log for a
// failed enrollment attempt: "InvalidJSONStructure('Credential missing
// required id')", with the error keyed under `errors.response` in the
// challenge response. That means Authentik expects the WHOLE credential
// object nested under a field literally called `response` — submitting
// the credential's own fields flat at the top level (as this used to)
// meant Authentik read credentialToJSON's own inner `response` sub-object
// (clientDataJSON/attestationObject) AS IF it were the whole credential,
// which naturally has no `id` of its own — matching the exact error seen.
// Wrapped here, once, so every caller (enrollment now, login-time
// validation once a passkey exists to test it with) automatically gets
// the right shape instead of each one needing to remember this.
function toStepSubmission(credential) {
  return { response: credentialToJSON(credential) };
}

// Runs the actual "create a passkey" browser ceremony (Touch ID/Windows
// Hello/security key prompt) and returns an object ready to POST back to
// /account/add-webauthn/step.
async function createWebauthnCredential(registration) {
  const credential = await navigator.credentials.create({ publicKey: decodeCreationOptions(registration) });
  return toStepSubmission(credential);
}

// Runs the "use your passkey" browser ceremony at login time and returns
// an object ready to submit alongside selected_challenge to
// /auth/login/step.
async function getWebauthnAssertion(assertion) {
  const credential = await navigator.credentials.get({ publicKey: decodeRequestOptions(assertion) });
  return toStepSubmission(credential);
}

export { isWebauthnSupported, createWebauthnCredential, getWebauthnAssertion };
